import React from 'react';

import { makeStyles, createStyles, Theme } from '@material-ui/core';
import { Typography } from '@cube3/ui/src/typography/Typography';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import Thumbnail from '@cube3/ui/src/thumbnails/Thumbnail';
import { Divider } from '@cube3/ui/src/Surface/Divider';
import { Shimmer } from '@cube3/cubicle/src/core/atoms/Loader/Shimmer';
import { isMobile } from 'react-device-detect';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';

interface Props<T = any> {
  logo: string | React.ReactNode;
  name: string;
  description?: string;
  mainButtonClick?(): void;
  mainButtonLabel?: string;
  accountButtonClick?(acc: T): void;
  accountButtonLabel?: string;
  accounts: T[];
  makeCols?(T): string[];
  loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 8,
      paddingTop: '20px',
      paddingBottom: '20px'
    },
    row: {
      display: 'flex',
      alignItems: 'center'
    },
    secondaryRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      width: '100%'
    },
    left: {
      width: '30%'
    },
    middle: {
      width: isMobile ? '55%' : '40%',
      marginRight: 20
    },
    right: {
      marginLeft: 'auto'
    },
    thumbnail: {
      width: 64,
      marginRight: 20,
      borderRadius: theme.surfaceCorners.mediumEmphasis,
      overflow: 'hidden'
    },
    connected: {
      marginLeft: isMobile ? 65 : 84,
      paddingTop: 8
    },
    account: {
      paddingTop: 8
    }
  })
);

export const ConnectedAccountsListingBlock: React.FC<Props> = (props) => {
  const {
    logo,
    name,
    description,
    mainButtonClick,
    mainButtonLabel = 'Connect account',
    accountButtonClick,
    accountButtonLabel = 'Disconnect',
    accounts,
    makeCols = (ca) => (ca ? [ca.display_name] : undefined),
    loading
  } = props;

  const classes = useStyles();

  if (loading) {
    return (
      <div className={classes.root}>
        <Shimmer height={32} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <div className={classes.thumbnail}>
          {typeof logo === 'string' ? (
            <Thumbnail
              src={logo}
              ratio="oneToOne"
              cover={true}
              showCheckerboard={false}
            />
          ) : (
            logo
          )}
        </div>

        <div className={classes.middle}>
          <Typography color="contrast1" variant="heading2">
            {name}
          </Typography>
          {description && (
            <Typography color={'contrast2'}>{description}</Typography>
          )}
        </div>

        {mainButtonClick && (
          <span className={classes.right}>
            <Button
              background="primary"
              buttonStyle="ghost"
              label={mainButtonLabel}
              onClick={mainButtonClick}
              size={isMobile ? SizeVariants.lg : SizeVariants.md}
            />
          </span>
        )}
      </div>
      <div className={classes.connected}>
        {accounts?.map((ca, idx, arr) => {
          return (
            <>
              <Divider />
              <div className={`${classes.account} ${classes.row}`}>
                <div className={classes.secondaryRow}>
                  {makeCols(ca)?.map((c) => (
                    <div
                      key={c}
                      className={!isMobile ? classes.middle : undefined}
                    >
                      <Typography>{c}</Typography>
                    </div>
                  ))}
                </div>
                {!makeCols(ca) && <Shimmer height={32} width={128} />}
                {accountButtonClick && (
                  <span className={classes.right}>
                    <Button
                      disabled={!ca || !makeCols(ca)}
                      background="critical"
                      buttonStyle="ghost"
                      label={accountButtonLabel}
                      onClick={() => accountButtonClick(ca)}
                      size={isMobile ? SizeVariants.lg : SizeVariants.md}
                    />
                  </span>
                )}
              </div>
            </>
          );
        })}
      </div>
    </div>
  );
};
